/**
 * Hook de base pour tous les widgets Brydge
 *
 * Centralise la logique commune partagée entre les widgets :
 * - Récupération des données entreprise
 * - Extraction de la couleur primaire
 * - Gestion du redimensionnement iframe
 * - Handler de fermeture du widget
 *
 * @module hooks/useWidgetBase
 */

import {useEffect, useCallback, useMemo} from 'react';
import {useIframeResize} from '../contexts/IframeResizeContext';
import {useExternalCompanyDetails} from 'packages/common/hooks/api/widgets/company/Company';
import {sendCloseMessage} from '../utils/iframeMessaging';
import {TIMING} from '../../../constants/timing';

/**
 * Configuration par défaut du hook useWidgetBase
 * @typedef {Object} UseWidgetBaseConfig
 * @property {string} companyId - Identifiant de l'entreprise
 * @property {string} apiKey - Clé API pour l'authentification
 * @property {boolean} embedded - Indique si le widget est dans une iframe
 * @property {string} displayMode - Mode d'affichage ('fixed' ou 'floating')
 * @property {string} instanceId - Identifiant unique de l'instance
 * @property {string} [customColor] - Couleur personnalisée optionnelle
 * @property {Array} [heightDependencies] - Dépendances supplémentaires pour le resize
 * @property {boolean} [skipHeightUpdate] - Désactiver la mise à jour automatique de hauteur
 */

/**
 * Valeur de retour du hook useWidgetBase
 * @typedef {Object} UseWidgetBaseResult
 * @property {React.RefObject} containerRef - Référence au conteneur pour mesure hauteur
 * @property {Function} updateHeight - Fonction pour déclencher une mise à jour de hauteur
 * @property {Function} forceResize - Fonction pour forcer un redimensionnement immédiat
 * @property {Object|null} companyDetails - Données de l'entreprise
 * @property {boolean} isCompanyLoading - État de chargement des données entreprise
 * @property {boolean} isCompanyError - Indique si une erreur s'est produite
 * @property {Error|null} companyError - Objet erreur le cas échéant
 * @property {Function} handleCloseWidget - Handler pour fermer le widget
 * @property {string|null} primaryColor - Couleur primaire calculée
 */

/**
 * Hook de base fournissant les fonctionnalités communes à tous les widgets
 *
 * @param {UseWidgetBaseConfig} config - Configuration du hook
 * @return {UseWidgetBaseResult} Objet contenant les utilitaires et données communes
 *
 * @example
 * const {
 *   containerRef,
 *   updateHeight,
 *   companyDetails,
 *   isCompanyLoading,
 *   companyError,
 *   handleCloseWidget,
 *   primaryColor,
 * } = useWidgetBase({
 *   companyId: '123',
 *   apiKey: 'abc',
 *   embedded: true,
 *   displayMode: 'fixed',
 *   instanceId: 'widget-1',
 *   customColor: '#FF5500',
 *   heightDependencies: [currentStep, formData],
 * });
 */
export function useWidgetBase({
  companyId,
  apiKey,
  embedded,
  displayMode,
  instanceId,
  customColor = null,
  heightDependencies = [],
  skipHeightUpdate = false,
}) {
  const {containerRef, updateHeight, forceResize} = useIframeResize();

  // Récupération des données entreprise via l'API
  const {
    companyDetails,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
  } = useExternalCompanyDetails(companyId, apiKey);

  // Calcul de la couleur primaire avec fallback chain
  const primaryColor = useMemo(() => {
    if (customColor) return customColor;
    if (companyDetails?.branding?.primaryColor) return companyDetails.branding.primaryColor;
    if (companyDetails?.primaryColor) return companyDetails.primaryColor;
    return null;
  }, [customColor, companyDetails?.branding?.primaryColor, companyDetails?.primaryColor]);

  // Handler de fermeture du widget (mode floating)
  const handleCloseWidget = useCallback(() => {
    if (embedded) {
      sendCloseMessage(instanceId);
    }
  }, [embedded, instanceId]);

  // Effet de mise à jour de la hauteur lors des changements de contenu
  useEffect(() => {
    if (skipHeightUpdate || !embedded) return;

    const timer = setTimeout(() => {
      updateHeight();
    }, TIMING.CONTENT_CHANGE_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, displayMode, updateHeight, skipHeightUpdate, ...heightDependencies]);

  return {
    // Références et fonctions de resize
    containerRef,
    updateHeight,
    forceResize,

    // Données entreprise
    companyDetails,
    isCompanyLoading,
    isCompanyError,
    companyError,

    // Handlers
    handleCloseWidget,

    // Thème
    primaryColor,
  };
}

export default useWidgetBase;
